import React, { useEffect, useState } from "react";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import AppHoc from "../../../../../../generics/AppHOC";
import Spinner from "../../../../../../common/Spinner";
import ErrorComponent from "../../../../../../common/ErrorComponent";
import NoDataAvailable from "../../../../../../common/NoDataAvailable";
import ApiService from "../../../../../../config/api-config";
import { ad_single_site_payload } from "../../../../../../config/reqConfig/anomalyDetection";
import css from "../../common/anomalyMagneticStyle.less";
import { PropTypes } from "prop-types";
import * as actions from "../../actions";
import ADBarChart from "./ADBarChart";
import ADTable from "./ADTable";
import CustomTimeStamp from "../../../../../../generics/TimeStamp/customTimeStamp";
import {
  convertTsToLocal,
  displayPathAnalyticsTime,
  backtoSevenDays,
  displayDateTime
} from "../../../../../../utils/displayTime";
import useI18n from '../../../../../../i18n/useI18n';
import ChartTableView from "./ChartTableView";

const [HbrSwitch] = reactWrapper(["hbr-switch"]);

const mapStateToProps = state => {
  return {
    adKey: state.vanalytics.adKey
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

const ADChartView = props => {
  const { tViews } = useI18n('views')
  const [chartData, setChartData] = useState([]);
  const [chartDataLoader, setChartDataLoader] = useState(true);
  const [chartDataError, setChartDataError] = useState("");
  const [selectedHour, setSelectedHour] = useState(null);
  const [timeFrame, setTimeFrame] = useState(null);
  const [chartDataLoaded, setChartDataLoaded] = useState(false);
  const [liveMessage, setLiveMessage] = useState("");
  const [accessibleView, setAccessibleView] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0, "smooth");
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setChartDataLoader(true);
      setChartDataError(null);
      setSelectedHour(null);
      try {
        // fetch data
        let timePeriod = backtoSevenDays();
        setTimeFrame(timePeriod);
        const payload = ad_single_site_payload(
          timePeriod,
          props.selectType === "topSites"
        );
        const res = await ApiService.getADSitesDistribution(payload);
        let data = [];
        if (!res.errorObject) {
          res.data.data.forEach(d => {
            let objEntry = {
              entry_ts: convertTsToLocal(d.entry_ts)
            };
            if (d.anomaly_rate !== "-")
              objEntry[tViews('predictiveNetworks.anomalyRateOnly')] = d.anomaly_rate;
            data.push(objEntry);
          });
        } else if (res.errorObject) {
          setSelectedHour(null);
          props.actions.saveDateForTable(null);
          setChartDataError(res.errorObject);
          setChartDataLoader(false);
        }
        let hour = data.length > 0 ? data[data.length - 2].entry_ts : null;
        props.actions.saveDateForTable(displayPathAnalyticsTime(hour));
        setSelectedHour(displayPathAnalyticsTime(hour));
        setChartData(data);
        setChartDataLoader(false);
        setChartDataLoaded(true);
      } catch (error) {
        setSelectedHour(null);
        setChartDataError(error);
      } finally {
        setChartDataLoader(false);
        setChartDataLoaded(true);
      }
    };
    fetchData();
  }, [props.selectType, props.globalFilter.onAnomalyRefresh]);

  useEffect(() => {
    if (props.adKey.adDate) setSelectedHour(props.adKey.adDate);
  }, [props.adKey.adDate]);

  useEffect(() => {
    if (chartDataLoader) {
      setLiveMessage("");
    } else if (!chartDataLoader && chartDataLoaded) {
      const timeout = setTimeout(() => {
        setLiveMessage(`${tViews('a11yLabels.loadingCompleted')}`);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [chartDataLoader, chartDataLoaded]);

  return (
    <>
      <div
        aria-live={tViews('a11yLabels.ariaLiveText')}
        className={css["aria-live-style"]}
      >
        {liveMessage}
      </div>
      <div className={css["anomaly-widget"]}>
        {chartDataLoader ? (
          <Spinner />
        ) : chartDataError ? (
          <ErrorComponent />
        ) : chartData?.length === 0 ? (
          <NoDataAvailable />
        ) : (
          <div className="ad-stack-chart">
            <div className="timestamp">
              <CustomTimeStamp
                prevTime={displayDateTime(timeFrame[0])}
                currentTime={displayDateTime(timeFrame[1])}
              />
              <div className="ad-timePeriodTxt">{`${tViews('app360.afHeatmapTimePriodText')}`}</div>
              <div className="ad-accessibilitySwitch">
                <HbrSwitch
                  checked={accessibleView}
                  onHbr-click={() => setAccessibleView(!accessibleView)}
                >
                  {tViews('tableView')}
                </HbrSwitch>
              </div>
            </div>
            {accessibleView ? (
              <ChartTableView
                data={chartData}
                saveData={props.actions.saveDateForTable}
                selectedBar={selectedHour}
              />
            ) : (
              <ADBarChart
                data={chartData}
                saveData={props.actions.saveDateForTable}
                selectedBar={selectedHour}
                id={"ad-site-chart"}
                titleY={tViews('app360.afAnomalyRate')}
                selectType={props.selectType}
              />
            )}
            {selectedHour && (
              <ADTable
                selectedHour={selectedHour}
                selectType={props.selectType}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};

ADChartView.propTypes = {
  globalFilter: PropTypes.object,
  selectType: PropTypes.string,
  actions: PropTypes.object,
  adKey: PropTypes.object,
  setSelectedSite: PropTypes.func
};

export default (
  connect(mapStateToProps, mapDispatchToProps)(AppHoc(ADChartView))
);
